import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Link, Copy, Check, Trash2, Download, FileText, X, AlertCircle } from 'lucide-react';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB per chunk

const fmtSize = (bytes) => {
  if (bytes === undefined || bytes === null) return '—';
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 ** 2)   return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3)   return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const randHex = (n) =>
  Array.from(crypto.getRandomValues(new Uint8Array(n))).map(b => b.toString(16).padStart(2, '0')).join('');

export default function FileShare() {
  const [dragging,  setDragging]  = useState(false);
  const [selected,  setSelected]  = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);        // 0~100
  const [uploadInfo, setUploadInfo] = useState(null);   // { sent, total, speed, eta, chunk, chunks }
  const [result,    setResult]    = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [files,     setFiles]     = useState([]);
  const [error,     setError]     = useState('');
  const fileInputRef = useRef(null);
  const cancelRef    = useRef(false);

  const refreshFiles = useCallback(() => {
    fetch('/api/files').then(r => r.json()).then(setFiles).catch(() => {});
  }, []);

  useEffect(() => { refreshFiles(); }, [refreshFiles]);

  // ── 드래그 앤 드롭 ──────────────────────────────────────────
  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()  => setDragging(false);
  const onDrop      = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) { setSelected(file); setResult(null); setError(''); }
  };
  const onFileInput = (e) => {
    const file = e.target.files[0];
    if (file) { setSelected(file); setResult(null); setError(''); }
    e.target.value = '';
  };

  // ── 청크 업로드 ──────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selected || uploading) return;
    setUploading(true);
    setProgress(0);
    setError('');
    cancelRef.current = false;

    const uploadId   = randHex(16);
    const totalChunks = Math.ceil(selected.size / CHUNK_SIZE);
    const startTime  = Date.now();
    let bytesSent    = 0;

    try {
      for (let i = 0; i < totalChunks; i++) {
        if (cancelRef.current) throw new Error('CANCEL');

        const start = i * CHUNK_SIZE;
        const end   = Math.min(start + CHUNK_SIZE, selected.size);
        const chunk = selected.slice(start, end);

        const form = new FormData();
        form.append('uploadId',    uploadId);
        form.append('chunkIndex',  i);
        form.append('totalChunks', totalChunks);
        form.append('originalName', selected.name);
        form.append('chunk', chunk);

        const res = await fetch('/api/files/upload-chunk', { method: 'POST', body: form });
        if (!res.ok) {
          let msg = `청크 ${i} 업로드 실패 (${res.status})`;
          try { const d = await res.json(); msg = d.error || msg; } catch {}
          throw new Error(msg);
        }

        bytesSent = end;
        const elapsed = (Date.now() - startTime) / 1000;
        const speed   = elapsed > 0 ? bytesSent / elapsed : 0;
        const remaining = selected.size - bytesSent;
        const eta     = speed > 0 ? remaining / speed : 0;

        setProgress(Math.round((bytesSent / selected.size) * 95));
        setUploadInfo({
          sent:   bytesSent,
          total:  selected.size,
          speed,
          eta,
          chunk:  i + 1,
          chunks: totalChunks,
        });
      }

      if (cancelRef.current) throw new Error('CANCEL');

      // 파일 조합 요청
      setUploadInfo(prev => ({ ...prev, finalizing: true }));
      const fin = await fetch('/api/files/upload-finalize', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ uploadId, originalName: selected.name, totalChunks }),
      });
      if (!fin.ok) {
        let msg = `서버 오류 (${fin.status})`;
        try { const d = await fin.json(); msg = d.error || msg; } catch {}
        throw new Error(msg);
      }
      const data = await fin.json();
      if (!data.ok) throw new Error(data.error || '조합 실패');

      setProgress(100);
      const url = `${window.location.origin}/share/${data.key}`;
      setResult({ key: data.key, originalName: data.originalName, url });
      setSelected(null);
      setUploadInfo(null);
      refreshFiles();
    } catch (err) {
      if (err.message !== 'CANCEL') setError(err.message);
      setUploadInfo(null);
    } finally {
      setUploading(false);
      cancelRef.current = false;
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setProgress(0);
    setUploadInfo(null);
    setUploading(false);
  };

  // ── 링크 복사 ────────────────────────────────────────────────
  const copyLink = (key) => {
    const url = `${window.location.origin}/share/${key}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  // ── 파일 삭제 ────────────────────────────────────────────────
  const handleDelete = async (key, name) => {
    if (!window.confirm(`'${name}' 파일을 삭제하시겠습니까?\n공유 링크도 함께 만료됩니다.`)) return;
    await fetch(`/api/files/${key}`, { method: 'DELETE' });
    if (result?.key === key) setResult(null);
    refreshFiles();
  };

  // ── 진행 표시 텍스트 ─────────────────────────────────────────
  const progressLabel = () => {
    if (!uploadInfo) return `${progress}%`;
    if (uploadInfo.finalizing) return '파일 조합 중...';
    const speedMB = (uploadInfo.speed / 1024 / 1024).toFixed(1);
    const etaSec  = Math.round(uploadInfo.eta);
    const etaStr  = etaSec > 60
      ? `${Math.floor(etaSec / 60)}분 ${etaSec % 60}초`
      : `${etaSec}초`;
    return `${fmtSize(uploadInfo.sent)} / ${fmtSize(uploadInfo.total)}  ·  ${speedMB} MB/s  ·  남은 시간 ${etaStr}`;
  };

  const dropBorder = dragging ? '#185FA5' : selected ? '#059669' : '#d3d1c7';
  const dropBg     = dragging ? '#e8f0fb' : selected ? '#f0fdf4' : 'white';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 720, margin: '0 auto' }}>

      {/* ── 드래그 앤 드롭 존 ─────────────────────────────────── */}
      <div
        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
        onClick={() => !selected && !uploading && fileInputRef.current?.click()}
        style={{
          border: `2.5px dashed ${dropBorder}`,
          borderRadius: 18,
          padding: selected || uploading ? '36px 32px' : '64px 32px',
          textAlign: 'center',
          background: dropBg,
          cursor: selected || uploading ? 'default' : 'pointer',
          transition: 'all 0.2s',
          userSelect: 'none',
        }}
      >
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileInput} />

        {selected || uploading ? (
          <div>
            <FileText size={52} color={uploading ? '#185FA5' : '#059669'}
              style={{ margin: '0 auto 14px', display: 'block' }} />
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
              {selected?.name}
            </div>
            <div style={{ fontSize: 14, color: '#888' }}>{fmtSize(selected?.size)}</div>

            {/* 진행 바 */}
            {uploading && (
              <div style={{ marginTop: 24, maxWidth: 480, margin: '24px auto 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600 }}>
                  <span>
                    {uploadInfo?.finalizing
                      ? '서버에서 파일 조합 중...'
                      : `청크 ${uploadInfo?.chunk ?? 0} / ${uploadInfo?.chunks ?? '?'}`}
                  </span>
                  <span>{progress}%</span>
                </div>
                <div style={{ width: '100%', height: 12, background: '#e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    width: `${progress}%`, height: '100%', borderRadius: 8,
                    background: 'linear-gradient(90deg, #185FA5, #3B82F6)',
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>{progressLabel()}</div>
              </div>
            )}

            {!uploading && (
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22 }}>
                <button onClick={handleUpload}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 28px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  <Upload size={17} /> 업로드 시작
                </button>
                <button onClick={() => { setSelected(null); setError(''); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 18px', background: '#f5f5f5', color: '#555', border: '1px solid #e0e0e0', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  <X size={15} /> 취소
                </button>
              </div>
            )}

            {uploading && (
              <button onClick={handleCancel}
                style={{ marginTop: 16, padding: '9px 22px', background: 'white', color: '#DC2626', border: '1.5px solid #fca5a5', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                업로드 중단
              </button>
            )}
          </div>
        ) : (
          <div>
            <Upload size={52} color="#bbb" style={{ margin: '0 auto 16px', display: 'block' }} />
            <div style={{ fontSize: 17, fontWeight: 700, color: '#666' }}>파일을 여기에 드래그하거나</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#185FA5', marginTop: 2 }}>클릭하여 선택하세요</div>
            <div style={{ fontSize: 13, color: '#bbb', marginTop: 10 }}>
              고화질 사진, CAD 도면 등 대용량 파일 지원 (제한 없음)
            </div>
          </div>
        )}
      </div>

      {/* ── 오류 표시 ────────────────────────────────────────── */}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12, padding: '14px 18px' }}>
          <AlertCircle size={18} color="#DC2626" />
          <span style={{ fontSize: 14, color: '#DC2626', fontWeight: 600 }}>{error}</span>
          <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── 업로드 완료 결과 카드 ──────────────────────────────── */}
      {result && (
        <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 16, padding: '22px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Check size={18} color="#059669" />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>업로드 완료!</span>
            <span style={{ fontSize: 14, color: '#555', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.originalName}</span>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>
            아래 링크를 거래처에 공유하면 로그인 없이 즉시 다운로드 가능합니다
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, background: 'white', border: '1px solid #d1fae5', borderRadius: 8, padding: '11px 14px', fontSize: 13, color: '#185FA5', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {result.url}
            </div>
            <button onClick={() => copyLink(result.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 20px', background: copiedKey === result.key ? '#059669' : '#185FA5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', transition: 'background 0.2s', flexShrink: 0 }}>
              {copiedKey === result.key ? <Check size={15} /> : <Copy size={15} />}
              {copiedKey === result.key ? '복사됨!' : '링크 복사'}
            </button>
          </div>
        </div>
      )}

      {/* ── 파일 목록 ────────────────────────────────────────── */}
      <div style={{ background: 'white', border: '1px solid #d3d1c7', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #f0f0f0', fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
          업로드된 파일 <span style={{ fontSize: 13, color: '#aaa', fontWeight: 400 }}>({files.length}개)</span>
        </div>

        {files.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#bbb', fontSize: 14 }}>
            아직 업로드된 파일이 없습니다
          </div>
        ) : (
          [...files].reverse().map((f, i) => (
            <div key={f.key}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 22px', borderBottom: i < files.length - 1 ? '1px solid #f8f8f8' : 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

              <FileText size={20} color="#94a3b8" style={{ flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.originalName}
                </div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {fmtSize(f.size)}&nbsp;·&nbsp;{fmtDate(f.uploadedAt)}&nbsp;·&nbsp;다운로드 {f.downloadCount || 0}회
                </div>
              </div>

              <button onClick={() => copyLink(f.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: copiedKey === f.key ? '#f0fdf4' : '#f8fafc', border: `1px solid ${copiedKey === f.key ? '#86efac' : '#e5e3db'}`, borderRadius: 7, fontSize: 12, color: copiedKey === f.key ? '#059669' : '#555', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {copiedKey === f.key ? <Check size={12} /> : <Link size={12} />}
                {copiedKey === f.key ? '복사됨' : '링크 복사'}
              </button>

              <a href={`/share/${f.key}`} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#f8fafc', border: '1px solid #e5e3db', borderRadius: 7, fontSize: 12, color: '#185FA5', textDecoration: 'none', fontWeight: 600, flexShrink: 0 }}>
                <Download size={12} /> 다운로드
              </a>

              <button onClick={() => handleDelete(f.key, f.originalName)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: '#ef4444', display: 'flex', flexShrink: 0 }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
